<aside class="sidebar" aria-label="Main navigation">
  <!-- Top section: brand + nav -->
  <div class="sidebar__top">
    <!-- Header with logo + divider -->
    <header class="sidebar__header">
      <a class="sidebar__logo" (click)="navigate.emit('/home'); closed.emit()" role="button" tabindex="0" aria-label="Go to Home">
        <img
          class="sidebar__logo-img"
          src="assets/icons/custos-logo-icon.svg"
          alt="Custos"
          width="24"
          height="26"
        />
        <img
          class="sidebar__logo-wordmark"
          src="assets/icons/custos-wordmark.svg"
          alt="Custos"
          width="51"
          height="9"
        />
      </a>
      <button
        class="sidebar__close-btn"
        (click)="closed.emit()"
        aria-label="Close navigation menu"
      >
        <app-icon name="close" [size]="20" />
      </button>
    </header>

    <!-- Navigation -->
    <nav class="sidebar__nav">
      <ul class="sidebar__nav-list" role="list">
        @for (item of navItems; track item.route) {
          <li role="listitem">
            <button
              class="sidebar__nav-item"
              [class.sidebar__nav-item--active]="!item.external && isActive(item.route)"
              (click)="onNavClick(item)"
              [attr.aria-current]="!item.external && isActive(item.route) ? 'page' : null"
            >
              <span class="sidebar__nav-item-inner">
                <img
                  class="sidebar__nav-icon"
                  [src]="item.icon"
                  alt=""
                  width="24"
                  height="24"
                />
                <span class="sidebar__nav-label">{{ item.label | translate }}</span>
                @if (item.external) {
                  <img
                    class="sidebar__nav-external-icon"
                    src="assets/icons/external-link.svg"
                    alt=""
                    width="14"
                    height="14"
                  />
                }
              </span>
            </button>
          </li>
        }
      </ul>
    </nav>
  </div>

  <!-- Bottom Section -->
  <div class="sidebar__bottom">
    <!-- Bottom nav items: language + theme + wallet (with top border divider) -->
    <div class="sidebar__bottom-nav">
      <!-- Language menu — always visible -->
      <button class="sidebar__bottom-row" [matMenuTriggerFor]="langMenu">
        <span class="sidebar__bottom-row-left">
          <app-icon class="sidebar__bottom-icon" name="globe" [size]="24" />
          <span class="sidebar__bottom-label">{{ 'SIDEBAR.LANGUAGE' | translate }}</span>
        </span>
        <app-icon class="sidebar__bottom-chevron" name="chevron-right" [size]="16" />
      </button>
      <mat-menu #langMenu="matMenu" xPosition="after" [overlapTrigger]="false">
        @for (lang of langOptions; track lang.value) {
          <button mat-menu-item (click)="selectLang(lang.value)" [class.sidebar__menu-active]="currentLang() === lang.value">
            {{ lang.label }}
          </button>
        }
      </mat-menu>

      <!-- Theme menu — always visible -->
      <button class="sidebar__bottom-row" [matMenuTriggerFor]="themeMenu">
        <span class="sidebar__bottom-row-left">
          @if (isDark()) {
            <app-icon class="sidebar__bottom-icon" name="moon" [size]="24" />
          } @else {
            <app-icon class="sidebar__bottom-icon" name="sun" [size]="24" />
          }
          <span class="sidebar__bottom-label">{{ isDark() ? ('SIDEBAR.DARK' | translate) : ('SIDEBAR.LIGHT' | translate) }}</span>
        </span>
        <app-icon class="sidebar__bottom-chevron" name="chevron-right" [size]="16" />
      </button>
      <mat-menu #themeMenu="matMenu" xPosition="after" [overlapTrigger]="false">
        <button mat-menu-item (click)="setTheme('light')" [class.sidebar__menu-active]="!isDark()">
          {{ 'SIDEBAR.LIGHT' | translate }}
        </button>
        <button mat-menu-item (click)="setTheme('dark')" [class.sidebar__menu-active]="isDark()">
          {{ 'SIDEBAR.DARK' | translate }}
        </button>
      </mat-menu>

      @if (walletService.isConnected()) {
        <!-- Chain indicator -->
        <div class="sidebar__chain-indicator"><span class="sidebar__chain-indicator-left">
          <span class="sidebar__chain-dot" [class.sidebar__chain-dot--wrong]="!walletService.isCorrectChain()"></span>
          {{ chainName() }}</span>
        </div>

        <!-- Wallet address row: fox + address + chevron → opens MatMenu -->
        <button
          class="sidebar__bottom-row"
          [matMenuTriggerFor]="walletMenu"
          [attr.aria-label]="'Wallet ' + walletService.shortAddress()"
        >
          <span class="sidebar__bottom-row-left">
            <span class="sidebar__bottom-label">{{ walletService.shortAddress() }}</span>
          </span>
          <app-icon class="sidebar__bottom-chevron" name="chevron-right" [size]="16" />
        </button>

        <mat-menu #walletMenu="matMenu" xPosition="after" [overlapTrigger]="false" class="wallet-menu">
          <!-- Portfolio -->
          <button mat-menu-item (click)="navigate.emit('/portfolio'); closed.emit()" class="wallet-menu__item">
            <app-icon name="wallet-card" [size]="16" />
            <span>{{ 'NAV.PORTFOLIO' | translate }}</span>
          </button>
          <!-- Invite Link -->
          <div class="wallet-menu__section">
            <span class="wallet-menu__label">{{ 'SIDEBAR.INVITE_LINK' | translate }}</span>
            @if (isLoggedIn()) {
              <span class="wallet-menu__link-row">
                <span class="wallet-menu__link-text">{{ inviteLink() }}</span>
                <button class="wallet-menu__copy-btn" type="button" (click)="copyInviteLink(); $event.stopPropagation()">
                  <app-icon [name]="inviteCopied() ? 'check' : 'copy'" [size]="16" />
                </button>
              </span>
            } @else {
              <!-- #I4/I5: don't expose a broken copy-button pre-login.
                   Offer explicit retry so a cancelled signing prompt isn't
                   a dead-end. -->
              <button
                class="wallet-menu__retry-btn"
                type="button"
                (click)="retryLogin(); $event.stopPropagation()"
                [disabled]="isLoggingIn()"
              >
                {{ isLoggingIn() ? ('COMMON.LOADING' | translate) : ('SIDEBAR.SIGN_IN_TO_GENERATE' | translate) }}
              </button>
              @if (authError()) {
                <span class="wallet-menu__error">{{ authError() }}</span>
              }
            }
          </div>
        </mat-menu>

        <!-- 退出 row -->
        <button
          class="sidebar__bottom-row sidebar__bottom-row--disconnect"
          (click)="onDisconnect()"
          aria-label="Disconnect wallet"
        >
          <span class="sidebar__bottom-label">{{ 'COMMON.DISCONNECT' | translate }}</span>
          <img src="assets/icons/logout-icon.svg" width="24" height="24" alt="" class="sidebar__bottom-icon sidebar__icon-invert" />
        </button>
      }
    </div>

    <!-- Mascot + Connect -->
    @if (!walletService.isConnected()) {
      <div class="sidebar__mascot-wrap">
        <div class="sidebar__mascot">
          <img
            class="sidebar__mascot-img"
            src="assets/images/metamask-fox.png"
            alt="MetaMask Wallet"
            width="68"
            height="64"
          />
          <button
            class="sidebar__connect-btn"
            (click)="onConnect()"
            [disabled]="walletService.isConnecting()"
          >
            {{ 'COMMON.CONNECT' | translate }}
          </button>
        </div>
      </div>
    }
  </div>
</aside>
